import { FontWeightEnum } from "../../types/AppTypes";
import { BodyTyp, TitlesTyp } from "../Typography/Typography";
import {
  MatrixGeneralRangeTotal,
  MatrixGeneralItem,
  MatrixGeneralScreenTotal,
  MatrixGeneralImpactTotal,
} from "../../types/api/potentialMatrix.type";
import { useAppSelector } from "../../hooks/store.hook";
import { Loader } from "../core/Loader/Loader";
import {
  IMPACT_LABEL,
  ImpactLevelEnum,
  MatrixSizes,
} from "../../constants/PotentialMatrixConstants";
import {
  StyledCellContentGloabal,
  StyledCircle,
  StyledCircleGroupContainer,
  StyledGeneralCell,
  StyledGeneralCellContainer,
  StyledGeneralMatrix,
  StyledImpactBracket,
  StyledImpactGroup,
} from "./MatrixGeneralGrid.styled";
import {
  BadgeChip,
  BadgeLabelSize,
  BadgeVariant,
} from "../BadgeChip/BadgeChip";
import { StyledThresholdsLegend } from "../../screens/PotentialMatrixV3/PerformanceMatrix/PerformanceChart/PerformanceChart.styled";
import { EMPTY_STRING } from "../../constants/Dictionary";

enum GroupEnum {
  LOW = "low",
  MID = "mid",
  HIGH = "high",
}

const CircleGroupMap: Record<GroupEnum, number> = {
  [GroupEnum.LOW]: 40,
  [GroupEnum.MID]: 70,
  [GroupEnum.HIGH]: 100,
};

type MatrixGeneralGridProps = {
  data: MatrixGeneralItem[];
  screenTotals: MatrixGeneralScreenTotal[];
  rangeTotals: MatrixGeneralRangeTotal[];
  impactTotals: MatrixGeneralImpactTotal[];
  size: MatrixSizes;
  yearSelected: number;
  loading?: boolean;
};

export const MatrixGeneralGrid = ({
  data,
  screenTotals,
  rangeTotals,
  impactTotals,
  size,
  yearSelected,
  loading = false,
}: MatrixGeneralGridProps) => {
  const { config } = useAppSelector((state) => state.config);

  const potentialMatrixConfig = config.find(
    (item) => item.year === yearSelected,
  )?.config?.finalInterviewDimensions;

  if (!potentialMatrixConfig) {
    return null;
  }

  const SCREEN_LABELS = [
    {
      label: "A",
      color: "#3A219E",
    },
    {
      label: "B",
      color: "#7152EC",
    },
    {
      label: "C",
      color: "#A794F3",
    },
    {
      label: "D",
      color: "#D0C5F9",
    },
  ];

  const IMPACTS = Object.values(ImpactLevelEnum);

  const getGroupForValue = (
    values: number[] = [0, 100],
    value: number,
  ): GroupEnum => {
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min;

    if (range === 0) {
      return GroupEnum.MID;
    }

    const step = range / 3;

    if (value < min + step) {
      return GroupEnum.LOW;
    } else if (value < min + 2 * step) {
      return GroupEnum.MID;
    } else {
      return GroupEnum.HIGH;
    }
  };

  const allCounts = data.map((item) => item.count);

  const renderBubble = (item: MatrixGeneralItem) => {
    const group = getGroupForValue(allCounts, item.count);

    return (
      <StyledCellContentGloabal>
        <StyledCircleGroupContainer $size={CircleGroupMap[GroupEnum.HIGH]}>
          <StyledCircle
            $size={CircleGroupMap[GroupEnum.HIGH]}
            $isVisible={group === GroupEnum.HIGH}
          />
          <StyledCircle
            $size={CircleGroupMap[GroupEnum.MID]}
            $isVisible={group === GroupEnum.MID || group === GroupEnum.HIGH}
          />
          <StyledCircle $size={CircleGroupMap[GroupEnum.LOW]} $isVisible={true}>
            <BodyTyp variant="1" fontWeight={FontWeightEnum.SEMIBOLD}>
              {item.count}
            </BodyTyp>
          </StyledCircle>
        </StyledCircleGroupContainer>
      </StyledCellContentGloabal>
    );
  };

  const getItemData = (
    screen: string,
    impact: ImpactLevelEnum,
    dimensionId: number,
  ) => {
    return data.find(
      (d) =>
        d.screen === screen &&
        d.impact === impact &&
        d.dimensionId === dimensionId,
    );
  };

  const getRowScreenTotal = (screen: string): number =>
    screenTotals.find((stat) => stat.screen === screen)?.count ?? 0;

  const getColumnRangeTotal = (
    impact: ImpactLevelEnum,
    dimensionId: number,
  ): number => {
    return (
      rangeTotals.find(
        (stat) => stat.impact === impact && stat.dimensionId === dimensionId,
      )?.count ?? 0
    );
  };

  const getImpactTotal = (impact: ImpactLevelEnum): number =>
    impactTotals.find((stat) => stat.impact === impact)?.count ?? 0;

  return (
    <StyledGeneralMatrix $size={size}>
      <Loader loading={loading} size={20}>
        <div className="matrix-general-full-grid">
          {SCREEN_LABELS.map((s) => (
            <>
              <div className="row-title-cell">
                <BodyTyp fontWeight={FontWeightEnum.SEMIBOLD} variant="1">
                  {s.label}
                </BodyTyp>
                <BadgeChip colorVariant={BadgeVariant.PRIMARY}>
                  # {getRowScreenTotal(s.label)}
                </BadgeChip>
              </div>
              {IMPACTS.flatMap((impact) =>
                potentialMatrixConfig.map((c) => {
                  const item = getItemData(s.label, impact, c.id);
                  return (
                    <StyledGeneralCell
                      key={`${s.label}-${impact}-${c.id}`}
                      $bgColor={s.color}
                      $size={size}
                    >
                      <StyledGeneralCellContainer
                        {...(item && {
                          $clickable: true,
                        })}
                      >
                        {item && item.count > 0 && <>{renderBubble(item)}</>}
                      </StyledGeneralCellContainer>
                    </StyledGeneralCell>
                  );
                }),
              )}
            </>
          ))}

          <div className="axis-row-spacer" />
          {IMPACTS.flatMap((impact) =>
            potentialMatrixConfig.map((v) => (
              <div className="axis-cell" key={`axis-${impact}-${v.id}`}>
                <BodyTyp fontWeight={FontWeightEnum.SEMIBOLD} variant="2">
                  {v.initials}
                </BodyTyp>
                <BadgeChip colorVariant={BadgeVariant.PRIMARY}>
                  # {getColumnRangeTotal(impact, v.id)}
                </BadgeChip>
              </div>
            )),
          )}

          <div className="axis-row-spacer" />
          {IMPACTS.map((impact, index) => {
            const isLastGroup = index === IMPACTS.length - 1;
            const isFirstGroup = index === 0;
            return (
              <>
                <StyledImpactGroup $isFirst={isFirstGroup}>
                  <StyledImpactBracket />
                  <div className="impact-label-content">
                    <TitlesTyp fontWeight={FontWeightEnum.SEMIBOLD} variant="3">
                      {IMPACT_LABEL[impact]}
                    </TitlesTyp>
                    <BadgeChip colorVariant={BadgeVariant.SECONDARY}>
                      # {getImpactTotal(impact)}
                    </BadgeChip>
                  </div>
                </StyledImpactGroup>
                {!isLastGroup}
              </>
            );
          })}
        </div>
      </Loader>
      <StyledThresholdsLegend>
        {potentialMatrixConfig.map((dimension) => (
          <BadgeChip size={BadgeLabelSize.MEDIUM}>
            {dimension.range.length === 2
              ? `${dimension.initials} = ${dimension.range[0]}-${dimension.range[1]}%`
              : `${dimension.initials} | ${EMPTY_STRING}`}
          </BadgeChip>
        ))}
      </StyledThresholdsLegend>
    </StyledGeneralMatrix>
  );
};
